import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/db';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../services/firebase';
import Header from '../components/Header';
import './ProfilePage.css';

function ProfilePage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State for form data
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    phoneNumber: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Error and success messages
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Loading state
  const [loading, setLoading] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('profile');
  
  // Load user data on component mount
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    setFormData(prevData => ({
      ...prevData,
      fullName: currentUser.fullName || '',
      username: currentUser.username || '',
      email: currentUser.email || '',
      phoneNumber: currentUser.phoneNumber || ''
    }));
  }, [currentUser, navigate]);
  
  // Clear messages after 5 seconds
  useEffect(() => {
    let timer;
    if (error || success) {
      timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [error, success]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  // Clear messages
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };
  
  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (!currentUser) {
      setError('You must be logged in to update your profile');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await userService.updateProfile(currentUser.id, {
        fullName: formData.fullName,
        username: formData.username,
        phoneNumber: formData.phoneNumber
      });
      
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess('Profile updated successfully!');
      }
    } catch (error) {
      setError('Failed to update profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password reset
  const handlePasswordReset = async () => {
    clearMessages();
    
    if (!currentUser || !currentUser.email) {
      setError('No email address associated with this account');
      return;
    }
    
    setLoading(true);
    
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setSuccess('Password reset email sent. Check your inbox.');
    } catch (error) {
      setError('Failed to send password reset email: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    clearMessages();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (formData.newPassword.length < 6) {
      setError('Password should be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await userService.changePassword(
        formData.currentPassword,
        formData.newPassword
      );
      
      if (result.error) {
        setError(result.error);
      } else {
        setSuccess('Password changed successfully!');
        setFormData(prevData => ({
          ...prevData,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      }
    } catch (error) {
      setError('Failed to change password: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="profile-page">
      <Header />
      
      <div className="profile-container">
        <div className="profile-header">
          <h1>My Profile</h1>
          <p>Manage your account information and security settings</p>
        </div>
        
        <div className="profile-tabs">
          <button 
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile Information
          </button>
          <button 
            className={`tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            Security
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        {activeTab === 'profile' && (
          <div className="profile-section">
            <form onSubmit={handleProfileUpdate}>
              <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Your full name"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Your username"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  disabled
                  placeholder="Your email"
                />
                <small>Email cannot be changed</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="Your phone number"
                />
              </div>
              
              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </form>
          </div>
        )}
        
        {activeTab === 'security' && (
          <div className="security-section">
            <div className="password-reset-card">
              <h3>Reset Password</h3>
              <p>We'll send you an email with a link to reset your password.</p>
              <button 
                onClick={handlePasswordReset} 
                className="reset-password-btn"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
            
            <div className="password-change-card">
              <h3>Change Password</h3>
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    placeholder="Your current password"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="New password"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="change-password-btn"
                  disabled={loading}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfilePage; 